import { memo } from "react";
import Link from "next/link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Image from "next/legacy/image";

import { getFirstLongParagraph } from "@/utils/general-functions";

const TITLE_LINES = 2;
const INTRO_LINES = 3;
const TITLE_LINE_HEIGHT = 1.4;
const INTRO_LINE_HEIGHT = 1.6;

const clampToLines = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const buildTitleSx = (fontPx, compact) => ({
  fontWeight: "bold",
  my: compact ? 1 : 2,
  color: "#111827",
  fontSize: `${fontPx}px`,
  lineHeight: TITLE_LINE_HEIGHT,
  ...clampToLines(TITLE_LINES),
  minHeight: `calc(${TITLE_LINE_HEIGHT} * ${fontPx}px * ${TITLE_LINES})`,
});

const buildIntroSx = (fontPx) => ({
  color: "#4B5563",
  fontSize: `${fontPx}px`,
  lineHeight: INTRO_LINE_HEIGHT,
  mb: 2,
  ...clampToLines(INTRO_LINES),
  minHeight: `calc(${INTRO_LINE_HEIGHT} * ${fontPx}px * ${INTRO_LINES})`,
});

/*
 * Both variants are built once instead of on every card render — a blog listing
 * renders ten of these and re-renders on every keystroke in the search box.
 */
const TITLE_SX = { regular: buildTitleSx(18, false), compact: buildTitleSx(14, true) };
const INTRO_SX = { regular: buildIntroSx(14), compact: buildIntroSx(12) };

/* Preview length of the intro excerpt, per breakpoint. */
const EXCERPT = {
  small: { minLen: 50, previewLen: 100 },
  large: { minLen: 100, previewLen: 150 },
};

const imageSx = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const footerSx = {
  mt: "auto",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const bodySx = { flexGrow: 1, display: "flex", flexDirection: "column" };

function GeneralCard({
  href,
  img,
  title,
  intro,
  author,
  date,
  onAuthorClick,
  sx = {},
  imgWidth = 380,
  imgHeight = 265,
  compact = false,
  cardClassName = "",
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const variant = compact ? "compact" : "regular";
  const { minLen, previewLen } = isSmallScreen ? EXCERPT.small : EXCERPT.large;

  const excerpt = intro ? getFirstLongParagraph(intro, minLen, previewLen) : null;

  const handleAuthorClick = (event) => {
    if (!onAuthorClick) return;

    // The author sits inside the card, so keep the click off the card link.
    event.preventDefault();
    event.stopPropagation();

    onAuthorClick(author);
  };

  return (
    <Box
      className={cardClassName}
      sx={{
        ...sx,
        borderRadius: "8px",
        overflow: "hidden",
        p: compact ? 1 : 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Link href={href} style={{ textDecoration: "none", display: "block" }}>
        <Image
          quality={100}
          alt={title}
          src={img}
          width={imgWidth}
          height={imgHeight}
          style={imageSx}
        />
      </Link>

      <Box sx={bodySx}>
        <Typography sx={TITLE_SX[variant]}>{title}</Typography>

        {excerpt && (
          <Typography
            sx={INTRO_SX[variant]}
            dangerouslySetInnerHTML={{ __html: excerpt }}
          />
        )}
      </Box>

      <Box sx={footerSx}>
        {author && (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: compact ? "12px" : "14px",
              color: "#040921",
              cursor: onAuthorClick ? "pointer" : "default",
              textTransform: "capitalize",
            }}
            onClick={handleAuthorClick}
          >
            {author}
          </Typography>
        )}

        {date && (
          <Typography
            sx={{ fontSize: compact ? "12px" : "14px", color: "#686B7A" }}
          >
            {date}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/*
 * Listing pages re-render on every search keystroke and the blog detail page
 * re-renders on every scroll tick. Cards whose props did not change skip the
 * excerpt regex entirely — worth it only because callers pass a stable
 * `onAuthorClick`.
 */
export default memo(GeneralCard);
